import type { Txt2ImgArtists, Txt2ImgCats, Txt2ImgStyles } from "@/utils/constants";
import type { ModelTypes, NoliboxModelTypes } from "@/modules/editor/constant";
import type { ThirdPartyMeta } from "@/thirdParty/meta";
import { Dictionary, INode, Lang, Logger } from "@noli/core";
import { checkAIMeta, checkMeta } from "@/utils/misc";

export type PromptInfo = {
  lang: Lang;
  original: string;
  originalNegative?: string;
  style?: Txt2ImgStyles;
  artist?: Txt2ImgArtists;
  // 高级模式
  promptSettings?: string[];
  negativePromptSettings?: string[];

  // specific prompts
  cat?: Txt2ImgCats;
};
export type CommonMetaData = {
  // 是否是「通过参数生成」的 meta
  isImport?: boolean;
  // 激活过的本地模型
  localModels?: Dictionary<string>;
  // 触发任务的时间戳
  timestamp?: number;
  // 当前节点的「上一级」节点
  from?: MetaType;
};

export type NoliboxMetaModelType = "basic" | "anime" | "art" | "general" | "real" | "3danime";
export type Txt2ImgMetaData = CommonMetaData & {
  w: number;
  h: number;
  // text 代指最终给到模型的 prompt
  text: string;
  seed: number;
  use_circular: boolean;
  is_anime?: boolean;
  num_steps?: number;
  guidance_scale?: number;
  negative_prompt?: string;
  version?: string; // 兼容数据，未来新生成的 meta 会记录为 model
  model?: NoliboxMetaModelType;
  sampler?: string;
  custom_embeddings?: string[];
  /**
   * 这里会记录一些用户的「原始输入信息」
   * 我们会通过 injectPrompt 方法，处理这些原始输入信息，并把最终得到的 prompt 注入到 text 字段中
   */
  promptInfo?: PromptInfo | undefined;
  /** 高清生成 */
  highres_info?: {
    fidelity?: number; // 高清时原图相似度
    upscale_factor?: number; // 放大程度
  };
};
export type Txt2ImgMeta = {
  type: "txt2img";
  data: Txt2ImgMetaData;
};

export type IGetTxt2ImgMetaData = {
  lang: Lang;
  text: string;
  circular: boolean;
  modelType: NoliboxModelTypes;
  displayNode: INode | null;
  originalNegative?: string;
  extraPromptInfo?: Partial<PromptInfo>;
  txt2ImgCustomSize?: { w: number; h: number };
  customEmbeddings?: string[];
  highResInfo?: Txt2ImgMetaData["highres_info"];
};

export type IGetImg2ImgMetaData = Omit<IGetTxt2ImgMetaData, "highResInfo"> & Img2ImgMetaData;

type Img2ImgMetaData = {
  url: string;
  fidelity: number;
};
export type Img2ImgMeta = {
  type: "img2img";
  data: Txt2ImgMetaData & Img2ImgMetaData;
};

export type VariationType = { seed: number; strength: number };
type VariationMetaData = {
  variations: VariationType[];
};
export type Txt2ImgVariationMeta = {
  type: "txt2img.variation";
  data: Txt2ImgMetaData & VariationMetaData;
};

export type Img2ImgVariationMeta = {
  type: "img2img.variation";
  data: Txt2ImgMetaData & Img2ImgMetaData & VariationMetaData;
};

export type Txt2ImgInpaintingMeta = {
  type: "txt2img.inpainting";
  data: Txt2ImgMetaData & { url: string; mask_url: string };
};

export type Txt2ImgOutpaintingMeta = {
  type: "txt2img.outpainting";
  data: Txt2ImgMetaData & { url: string };
};

export type AIMetaType =
  | Txt2ImgMeta
  | Img2ImgMeta
  | Txt2ImgVariationMeta
  | Img2ImgVariationMeta
  | Txt2ImgInpaintingMeta
  | Txt2ImgOutpaintingMeta;

export type UploadMeta = {
  type: "upload";
  data: CommonMetaData & {
    url: string;
    wh: { w: number; h: number };
    isDrag: boolean;
  };
};

export type PasteMeta = {
  type: "upload";
  data: CommonMetaData & {
    url: string;
    wh: { w: number; h: number };
    isPaste: boolean;
  };
};
export type DuplicateMeta = {
  type: "duplicate";
  data: CommonMetaData & {
    url: string;
    wh: { w: number; h: number };
    duplicateFrom: "album";
  };
};

export type MattMeta = {
  type: "SOD";
  data: CommonMetaData & {
    url: string;
    originUrl: string;
    cutoutUrl: string;
    background?: any;
    wh: { w: number; h: number };
  };
};

export type SuperResolutionMeta = {
  type: "sr";
  data: CommonMetaData & {
    url: string;
    wh: { w: number; h: number };
    max_wh: number;
    is_anime: boolean;
  };
};
export type InpaintingMeta = {
  type: "inpainting";
  data: CommonMetaData & {
    url: string;
    mask_url: string;
    wh: { w: number; h: number };
    use_refine?: boolean;
    refine_fidelity?: number;
    use_pipeline?: boolean;
  };
};
export type LandscapeSynthesisMeta = {
  type: "landscape";
  data: CommonMetaData & {
    url: string;
    wh: { w: number; h: number };
  };
};

export type MetaType =
  | AIMetaType
  | UploadMeta
  | PasteMeta
  | DuplicateMeta
  | SuperResolutionMeta
  | InpaintingMeta
  | LandscapeSynthesisMeta
  | ThirdPartyMeta
  | MattMeta;

export const allAIMetaTypes: AIMetaType["type"][] = [
  "txt2img",
  "img2img",
  "txt2img.variation",
  "img2img.variation",
  "txt2img.inpainting",
  "txt2img.outpainting",
];
export const allMetaTypes: MetaType["type"][] = (allAIMetaTypes as MetaType["type"][]).concat([
  "upload",
  "sr",
  "inpainting",
  "landscape",
  "wenxin",
  "duplicate",
  "SOD",
  "monkey",
  "draft",
]);
// 被画宇宙AI处理过的类型
export const processedMetaType: MetaType["type"][] = [
  "txt2img",
  "img2img",
  "txt2img.variation",
  "img2img.variation",
  "txt2img.inpainting",
  "txt2img.outpainting",
  "sr",
  "inpainting",
  "landscape",
  "SOD",
];

export function getOriginMeta(meta: any): MetaType | undefined {
  if (!checkMeta(meta)) return;
  if (checkAIMeta(meta) || !meta.data.from) return meta;
  return getOriginMeta(meta.data.from);
}
export type MetaMapper = (meta: MetaType) => any;
/**
 * @param meta MetaType
 * @param mapper 映射函数，也可以做一些操作，可能用不到
 * @returns 数组，包含传入的meta,其中传入的在数组末尾，原始的在数组头部
 */
export function getOriginOpChain(meta: any, mapper: MetaMapper = (meta) => meta): MetaType[] {
  const chain: MetaType[] = [];
  let curMeta: MetaType | undefined = meta;
  while (curMeta?.data && checkMeta(curMeta)) {
    chain.unshift(mapper(curMeta));
    curMeta = curMeta.data.from;
  }
  return chain;
}

export function getLatestValidMeta(meta: any): MetaType | undefined {
  if (!checkMeta(meta)) return;
  if (meta.type !== "duplicate") return meta;
  if (!meta.data.from) {
    Logger.error(">>> duplicate meta should have a from field!!");
    return;
  }
  // if (checkAIMeta(meta) || !meta.data.from) return meta;
  return getLatestValidMeta(meta.data.from);
}

export function getWHFromMeta(meta: MetaType): { w: number; h: number } {
  if (!checkAIMeta(meta)) {
    if (meta.data.wh) return meta.data.wh;
    const typeMeta = meta as unknown as Txt2ImgMeta;
    return { w: typeMeta.data.w, h: typeMeta.data.h };
  }
  if (meta.data.highres_info) {
    return {
      w: meta.data.w * (meta.data.highres_info.upscale_factor ?? 2),
      h: meta.data.h * (meta.data.highres_info.upscale_factor ?? 2),
    };
  }
  return { w: meta.data.w, h: meta.data.h };
}
